import { type PathValue, useFormContext } from "react-hook-form";

import {
  AI_VISIBILITY_STATUS_UI,
  getAiOptDisabled,
  getAiOptStatus,
} from "~/components/siteSettings/aiVisibilityStatus";
import { canMember } from "~/helpers/authorizations";

import Spinner from "../ui/spinner/Spinner";
import { AiVisibilityOptimizationsPanel } from "./AiVisibilityOptimizationsPanel";
import { SiteWithAuth } from "./General";
import type { SiteSettingsFormValues } from "./SiteSettings";

type Props = {
  siteData: SiteWithAuth | undefined;
  isLoading: boolean;
  hideInjectWidgetManualAlert?: boolean;
  hidePrerenderingToggle?: boolean;
  hideDisabledControls?: boolean;
};

export const AiVisibilityOptimizationsRHFAdapter = ({
  siteData,
  isLoading,
  hideInjectWidgetManualAlert,
  hidePrerenderingToggle,
  hideDisabledControls,
}: Props) => {
  const site = siteData?.site;

  const canUpdateSite = canMember(siteData?.authorizations, "update");
  const canEditCrawlSettings = canMember(siteData?.authorizations, "recrawl");

  const { watch, setValue } = useFormContext<SiteSettingsFormValues>();

  const status = getAiOptStatus(
    site?.site_speed_status,
    site?.site_speed_requested,
  );

  const statusUi = AI_VISIBILITY_STATUS_UI[status];

  if (isLoading) return <Spinner />;

  return (
    <AiVisibilityOptimizationsPanel
      status={status}
      notification={statusUi.notification}
      instructionText={statusUi.instructionText}
      hideInjectWidgetManualAlert={hideInjectWidgetManualAlert}
      savedPrerendering={!!site?.prerendering}
      integrationType={site?.delegated_domain?.integration_type}
      siteId={site?.id}
      fqdn={site?.fqdn}
      alt_fqdn={site?.alt_fqdn}
      hidePrerenderingToggle={hidePrerenderingToggle}
      hideDisabledControls={hideDisabledControls}
      values={{
        site_speed_requested: !!watch("site_speed_requested"),
        prerendering: !!watch("prerendering"),
        apex_redirect: !!watch("apex_redirect"),
        inject_widget: !!watch("inject_widget"),
        inject_markdown: watch("inject_markdown") ?? null,
        disable_lazy_automation: !!watch("disable_lazy_automation"),
        disable_defer_automation: !!watch("disable_defer_automation"),
        disable_preload_automation: !!watch("disable_preload_automation"),
      }}
      disabled={getAiOptDisabled(canUpdateSite, canEditCrawlSettings)}
      onToggle={(key, value) => {
        if (key === "site_speed_requested") {
          setValue("site_speed_requested", Boolean(value), {
            shouldDirty: true,
          });

          return;
        }

        // The panel keys map 1:1 onto the form fields, but RHF can't correlate
        // the union key with its value type, so cast to the matching field type.
        setValue(key, value as PathValue<SiteSettingsFormValues, typeof key>, {
          shouldDirty: true,
        });
      }}
    />
  );
};
